
import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { TelegramHybridButton } from '@/components/telegram-hybrid-button';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useApp } from '@/lib/context';
import { hideMainButton, isTelegramWebApp, hideBackButton, showBackButton } from '@/lib/telegram-service';

// Steps
import { Step1Date } from './steps/step1-date';
import { Step2Location } from './steps/step2-location';
import { Step3Categories } from './steps/step3-categories';

// Schema di validazione
const speedDateFormSchema = z.object({
  date: z.date(),
  location: z.object({
    city: z.string().min(2, 'Inserisci la città'),
    latitude: z.string(),
    longitude: z.string()
  }),
  allowedCategories: z.array(z.string()).min(1, 'Seleziona almeno una categoria'),
  description: z.string().max(500, 'La descrizione non può superare 500 caratteri').optional().default('')
});

type SpeedDateFormData = z.infer<typeof speedDateFormSchema>;

interface SpeedDateCreationFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function SpeedDateCreationForm({ onClose, onSuccess }: SpeedDateCreationFormProps) {
  // ===== 1. TUTTI GLI HOOKS ALL'INIZIO (in ordine costante) =====
  const [step, setStep] = useState(1);
  const [isNavigating, setIsNavigating] = useState(false);
  const totalSteps = 3;

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useApp();

  const form = useForm<SpeedDateFormData>({
    resolver: zodResolver(speedDateFormSchema),
    defaultValues: {
      date: undefined,
      location: {
        city: '',
        latitude: '',
        longitude: ''
      },
      allowedCategories: [],
      description: ''
    },
    mode: 'onChange',
  });

  const { isSubmitting } = form.formState;

  const mutation = useMutation({
    mutationFn: async (data: SpeedDateFormData) => {
      console.log('[SPEED DATE CREATION] 🚀 INIZIO PROCESSO CREAZIONE SPEED DATE');
      console.log('[SPEED DATE CREATION] 📦 Dati ricevuti:', JSON.stringify(data, null, 2));

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userTimezoneOffset = new Date().getTimezoneOffset();
      
      console.log('[SPEED DATE CREATION] 🌍 TIMEZONE DEBUG:', {
        originalDate: data.date,
        originalDateISO: data.date.toISOString(),
        userTimezone,
        userTimezoneOffset,
        offsetExplanation: `UTC${userTimezoneOffset > 0 ? '-' : '+'}${Math.abs(userTimezoneOffset / 60)}`
      });

      const speedDateTime = new Date(data.date);
      speedDateTime.setHours(12, 0, 0, 0);

      const speedDateData = {
        title: data.description ? data.description.substring(0, 100) : `Speed Date a ${data.location.city}`,
        description: data.description || '',
        date: speedDateTime.toISOString(),
        location: `${data.location.city}`,
        city: data.location.city,
        latitude: String(data.location.latitude),
        longitude: String(data.location.longitude),
        allowedCategories: data.allowedCategories || [],
        maxParticipants: 999,
        userTimezone: userTimezone,
        userTimezoneOffset: userTimezoneOffset
      };

      console.log('[SPEED DATE CREATION] 🌐 Dati da inviare:', JSON.stringify(speedDateData, null, 2));

      const responseData = await apiRequest('POST', '/api/speed-dates', speedDateData);
      
      return responseData;
    },
    onSuccess: (data) => {
      console.log('[SPEED DATE CREATION] Speed date creato con successo:', data);

      toast({
        title: 'Speed Date created!',
        description: 'Your speed date has been created successfully',
      });

      queryClient.invalidateQueries({ queryKey: ['/api/speed-dates'] });
      
      // Reset navigating state before closing
      setIsNavigating(false);
      
      // Close form after short delay to ensure state updates
      setTimeout(() => {
        onSuccess?.(); // Chiama callback se fornito
        onClose();
      }, 100);
    },
    onError: (error: Error) => {
      console.error('[SPEED DATE CREATION] Errore:', error);

      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
      
      // Reset navigating state on error
      setIsNavigating(false);
    }
  });

  useEffect(() => {
    if (isTelegramWebApp()) {
      hideMainButton();
      hideBackButton(); // Nascondi il back button all'inizio
    }

    return () => {
      if (isTelegramWebApp()) {
        hideMainButton();
        hideBackButton();
      }
    };
  }, []);

  // ===== 2. POI TUTTE LE FUNZIONI HELPER (dopo tutti gli hooks) =====
  const handleBackNavigation = () => {
    if (isNavigating || mutation.isPending) {
      return;
    }

    if (step > 1) {
      setStep(step - 1);
    } else {
      onClose();
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    try {
      switch (step) {
        case 1: {
          const date = form.getValues('date');

          if (!date) {
            toast({
              title: 'Date required',
              description: 'Select the speed date date',
              variant: 'destructive',
            });
            return false;
          }

          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const selectedDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const maxDate = new Date(today);
          maxDate.setDate(today.getDate() + 2);

          if (selectedDay < today || selectedDay > maxDate) {
            toast({
              title: 'Invalid date',
              description: 'Speed dates can only be created for today or the next 2 days',
              variant: 'destructive',
            });
            return false;
          }

          return true;
        }

        case 2: {
          const location = form.getValues('location');
          const descriptionValue = form.getValues('description');

          console.log('[VALIDATION STEP 2] location:', location);
          console.log('[VALIDATION STEP 2] descriptionValue:', descriptionValue, 'type:', typeof descriptionValue);

          if (!location?.city) {
            toast({
              title: 'Location required',
              description: 'Select the speed date city',
              variant: 'destructive',
            });
            return false;
          }

          // Safely check description with type checking and optional chaining
          const descriptionStr = String(descriptionValue || '');
          const trimmedDescription = descriptionStr.trim();

          console.log('[VALIDATION STEP 2] trimmedDescription:', trimmedDescription, 'length:', trimmedDescription.length);

          if (trimmedDescription.length < 10) {
            form.setError('description', {
              type: 'manual',
              message: 'Enter a description of at least 10 characters'
            });
            return false;
          }

          if (trimmedDescription.length > 500) {
            toast({
              title: 'Description too long',
              description: 'The description cannot exceed 500 characters',
              variant: 'destructive',
            });
            return false;
          }

          return true;
        }

        case 3: {
          const allowedCategories = form.getValues('allowedCategories');

          if (!allowedCategories || !allowedCategories.length) {
            toast({
              title: 'Categories required',
              description: 'Select at least one category',
              variant: 'destructive',
            });
            return false;
          }

          return true;
        }

        default:
          console.error('[SPEED DATE FORM] Step non valido:', step);
          return false;
      }
    } catch (error) {
      console.error('[SPEED DATE FORM] 💥 ERRORE in validateCurrentStep:', error);
      toast({
        title: 'Validation Error',
        description: 'An error occurred while validating the form',
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleNextStep = async () => {
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      const isValid = await validateCurrentStep();

      if (!isValid) {
        console.log('[SPEED DATE FORM] ⚠️ Validazione fallita, non procedo');
        setTimeout(() => setIsNavigating(false), 50);
        return;
      }

      console.log('[SPEED DATE FORM] ✅ Validazione OK, procedo');

      if (step === totalSteps) {
        const values = form.getValues();
        console.log('[SPEED DATE FORM] 📤 Invio dati al server:', values);
        try {
          await mutation.mutateAsync(values);
          // isNavigating viene resettato in onSuccess/onError della mutation
        } catch (mutationError) {
          console.error('[SPEED DATE FORM] 💥 Errore mutation:', mutationError);
          setIsNavigating(false);
        }
      } else {
        setTimeout(() => {
          setStep(step + 1);
          setIsNavigating(false);
        }, 100);
      }
    } catch (error) {
      console.error('[SPEED DATE FORM] 💥 ERRORE durante avanzamento:', error);

      toast({
        title: 'Errore',
        description: error instanceof Error ? error.message : 'Errore durante il salvataggio',
        variant: 'destructive',
      });

      setIsNavigating(false);
    }
  };

  const getButtonText = (): string => {
    return step === totalSteps
      ? (mutation.isPending ? 'Creating speed date...' : 'Create Speed Date')
      : 'Continue';
  };

  const isButtonDisabled = (): boolean => {
    return isSubmitting || isNavigating || mutation.isPending;
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <Step1Date form={form} />;
      case 2:
        return <Step2Location form={form} />;
      case 3:
        return <Step3Categories form={form} />;
      default:
        return null;
    }
  };

  // Gestisci il BackButton di Telegram
  useEffect(() => {
    if (!isTelegramWebApp()) return;

    showBackButton(handleBackNavigation);

    return () => {
      hideBackButton();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ===== 3. INFINE IL RETURN DEL JSX =====
  return (
    <div className="tg-bg h-full w-full max-w-md mx-auto px-4 relative min-h-screen flex flex-col" style={{ paddingTop: 'calc(90px + var(--safe-top))' }}>

      <div className="mb-6">
        <div className="flex gap-1 mb-4">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full ${
                i < step ? 'bg-[#2563eb]' : 'tg-secondary-bg'
              }`}
            />
          ))}
        </div>
      </div>

      <Form {...form}>
        <form className="space-y-6 flex-1">
          <div className="mb-24">
            {renderStep()}
          </div>

          <TelegramHybridButton
            text={getButtonText()}
            onClick={handleNextStep}
            visible={true}
            disabled={isButtonDisabled()}
            showProgress={isSubmitting}
            color="#2563eb"
            textColor="#ffffff"
          />
        </form>
      </Form>
    </div>
  );
}
